// apps/unizo/web/src/lib/rum.ts
// PRODUCT_DEV-493: CloudWatch RUM（フロントエラー/HTTP/Web Vitals 計測）。
// authenticated 専用: guest(匿名) は使わず、ログイン済ユーザの Cognito User Pool セッションを
// Identity Pool で AWS 一時クレデンシャルに連携し、awsRum.setAwsCredentials() で渡す。
// 未設定の環境（VITE_RUM_* 無し）や未ログイン時は no-op。
import { AwsRum, type AwsRumConfig } from "aws-rum-web";
import {
  CognitoIdentityClient,
  GetIdCommand,
  GetCredentialsForIdentityCommand,
} from "@aws-sdk/client-cognito-identity";
import { validSessionToken } from "@shared-ui/utils/token";
import { safeStringify } from "@/lib/navDiagnostics";
import { ENV } from "@/config/env";

let awsRum: AwsRum | undefined;
// Cognito 一時クレデンシャルの既定有効期限は1時間。50分ごとに更新する。
const CREDENTIAL_REFRESH_MS = 50 * 60 * 1000;

/** RUM を初期化（アプリ起動時に1回）。失敗してもアプリ本体に影響させない。 */
export function initRum(): void {
  const appMonitorId = ENV.RUM_APP_MONITOR_ID;
  const region = ENV.RUM_REGION;
  if (!appMonitorId || !region) return; // 未設定環境では無効
  try {
    const config: AwsRumConfig = {
      sessionSampleRate: 1, // 全環境100%（PRODUCT_DEV-493）
      // authenticated 専用: 組み込みの guest クレデンシャル取得を使わず、
      // ログイン後に setAwsCredentials() で federated creds を渡す。
      signing: true,
      telemetries: ["errors", "http", "performance"],
      allowCookies: true,
      enableXRay: false,
    };
    awsRum = new AwsRum(appMonitorId, "1.0.0", region, config);
    void refreshRumCredentials();
    setInterval(() => void refreshRumCredentials(), CREDENTIAL_REFRESH_MS);
  } catch {
    // 初期化失敗は無視（計測はベストエフォート）
  }
}

/** ログイン済セッションから Identity Pool 経由で AWS 一時クレデンシャルを取得し RUM へ設定。 */
async function refreshRumCredentials(): Promise<void> {
  if (!awsRum) return;
  const identityPoolId = ENV.RUM_IDENTITY_POOL_ID;
  const userPoolId = ENV.COGNITO_USERPOOL_ID;
  const region = ENV.RUM_REGION;
  if (!identityPoolId || !userPoolId || !region) return;

  try {
    const session = await validSessionToken();
    if (!session?.idToken) return; // 未ログインは送信しない（authenticated 専用）

    const provider = `cognito-idp.${region}.amazonaws.com/${userPoolId}`;
    const logins = { [provider]: session.idToken };
    const client = new CognitoIdentityClient({ region });

    const { IdentityId } = await client.send(
      new GetIdCommand({ IdentityPoolId: identityPoolId, Logins: logins }),
    );
    if (!IdentityId) return;

    const { Credentials } = await client.send(
      new GetCredentialsForIdentityCommand({ IdentityId, Logins: logins }),
    );
    if (!Credentials?.AccessKeyId || !Credentials.SecretKey) return;

    awsRum.setAwsCredentials({
      accessKeyId: Credentials.AccessKeyId,
      secretAccessKey: Credentials.SecretKey,
      sessionToken: Credentials.SessionToken,
      expiration: Credentials.Expiration,
    });
  } catch {
    // 連携失敗は無視（次回更新で再試行）
  }
}

/**
 * 任意のエラーを RUM に手動記録する（PRODUCT_DEV-612）。
 *
 * `telemetries: ["errors"]` が拾うのは未捕捉例外だけなので、「例外にはならないが
 * 明らかに異常」な状態（例: クライアントサイド遷移がコミットされない）を残すために使う。
 * RUM 未設定・未ログインの環境では no-op。
 *
 * **`meta` は message に畳み込む。** RUM の `js_error_event` に残るのは `type` /
 * `message` / `stack` だけで、`Error` に生やした任意プロパティは捨てられる
 * （2026-08-10 に実測。`stuck-navigation` が message だけで届き、meta が消えていた）。
 */
export function recordRumError(
  error: Error,
  meta?: Record<string, unknown>,
): void {
  try {
    if (!awsRum) return;
    if (!meta) {
      awsRum.recordError(error);
      return;
    }
    const enriched = new Error(`${error.message} ${safeStringify(meta)}`);
    enriched.name = error.name;
    enriched.stack = error.stack;
    awsRum.recordError(enriched);
  } catch {
    // 計測はベストエフォート。失敗してもアプリ本体に影響させない。
  }
}
